import {
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  TotpMultiFactorGenerator,
  TotpSecret,
  User,
  multiFactor,
} from 'firebase/auth';
import { AddButton, Button, CloseButton } from '@ui/shared/Button';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { getErrorMessage } from '../../../constants/firebase-auth-errors';
import {
  closeTopLevelPopup,
  openTopLevelPopup,
} from '../../../data/providers/popupsProvider';
import { userAsPromise } from '../../../data/providers/userProvider';
import { ISelectOption } from '../../../data/types';
import { Column } from '../../layout/Column';
import { CardBody } from '../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../shared/card/CardFooter';
import { CardHeader, CardHeaderLeft } from '../../shared/card/CardHeader';
import { ControlLabel } from '../../shared/form/ControlLabel';
import { ControlSpacer } from '../../shared/form/ControlSpacer';
import { ErrorText } from '../../shared/form/ErrorMessage';
import { FormContainer } from '../../shared/form/FormContainer';
import { TextField } from '../../shared/form/TextField';
import { SecondFactorSelect } from '../../shared/form/selects/SecondFactorSelect';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { InnerPopupCard } from '../../shared/popups/PopupCard';
import { QRCode } from './QRCode';
import { ReauthenticatePopup } from './ReauthenticatePopup';

interface IEnrollmentProps {
  onCancel: () => void;
  user: User;
}

enum EnrollmentState {
  CREATE_SECOND_FACTOR,
  VERIFY_SECOND_FACTOR,
}

export const EnrollmentPopup = withRouter(
  (props: IEnrollmentProps & RouteComponentProps): React.ReactElement => {
    const [displayName, setDisplayName] = React.useState<string>('');
    const [totpSecret, setTotpSecret] = React.useState<TotpSecret>();
    const [factorType, setFactorType] = React.useState<ISelectOption>({
      label: 'SMS',
      value: 'sms',
    });
    const [phoneNumber, setPhoneNumber] = React.useState<string>('');
    const [verificationCode, setVerificationCode] = React.useState<string>('');
    const verificationId = React.useRef<string | null>(null);
    const [error, setError] = React.useState<string>('');
    const [loading, setLoading] = React.useState<boolean>(false);
    const [currentState, setCurrentState] = React.useState<EnrollmentState>(
      EnrollmentState.CREATE_SECOND_FACTOR
    );

    const submit = async (e: React.FormEvent): Promise<void> => {
      e.preventDefault();
      e.stopPropagation();
      setLoading(true);
      if (factorType.value === 'sms') {
        const user = await userAsPromise();
        const recaptchaVerifier = new RecaptchaVerifier(
          user.services.auth,
          'recaptcha-container',
          {
            size: 'invisible',
            callback: (): void => null,
            'expired-callback': (): void => null,
          }
        );
        try {
          const session = await multiFactor(props.user).getSession();
          const phoneInfoOptions = {
            phoneNumber: `+${phoneNumber}`,
            session,
          };
          const phoneAuthProvider = new PhoneAuthProvider(user.services.auth);

          try {
            verificationId.current = await phoneAuthProvider.verifyPhoneNumber(
              phoneInfoOptions,
              recaptchaVerifier
            );
            setCurrentState(EnrollmentState.VERIFY_SECOND_FACTOR);
          } catch (err) {
            if (
              err.code === 'auth/requires-recent-login' ||
              err.error?.message === 'CREDENTIAL_TOO_OLD_LOGIN_AGAIN'
            ) {
              openTopLevelPopup(
                <ReauthenticatePopup
                  user={props.user}
                  onCancel={closeTopLevelPopup}
                />
              );
              return;
            }
            setError(getErrorMessage(err.code));
          }
        } catch (err) {
          setError(getErrorMessage(err.code));
        } finally {
          setLoading(false);
        }
      } else if (factorType.value === 'totp') {
        setCurrentState(EnrollmentState.VERIFY_SECOND_FACTOR);
        setLoading(false);
      }
    };

    const completeEnrollment = async (e: React.FormEvent): Promise<void> => {
      e.preventDefault();
      e.stopPropagation();
      if (factorType.value === 'totp') {
        try {
          const multiFactorAssertion =
            TotpMultiFactorGenerator.assertionForEnrollment(
              totpSecret,
              verificationCode
            );

          // Finalize the enrollment.
          await multiFactor(props.user).enroll(
            multiFactorAssertion,
            displayName
          );
          props.user.reload();
          props.onCancel();
        } catch (err) {
          if (
            err.code === 'auth/requires-recent-login' ||
            err.error?.message === 'CREDENTIAL_TOO_OLD_LOGIN_AGAIN'
          ) {
            openTopLevelPopup(
              <ReauthenticatePopup
                user={props.user}
                onCancel={closeTopLevelPopup}
              />
            );
            return;
          }
        }
      } else if (factorType.value === 'sms') {
        const cred = PhoneAuthProvider.credential(
          verificationId.current,
          verificationCode
        );
        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

        // Complete enrollment.
        try {
          await multiFactor(props.user).enroll(
            multiFactorAssertion,
            displayName
          );
          props.user.reload();
          props.onCancel();
        } catch (err) {
          setError(getErrorMessage(err.code));
        }
      }
    };
    /// require: displayName is set and either phoneNumber or totpSecret is set
    const factorSubmittable =
      displayName.length > 0 && (phoneNumber.length > 0 || totpSecret);

    const verificationSubmittable = verificationCode.length > 0;

    return (
      <InnerPopupCard size='medium' onClose={props.onCancel} noClickOutside>
        <Column>
          <CardHeader surface={8}>
            <CardHeaderLeft>
              <h5>Enroll Second Factor</h5>
            </CardHeaderLeft>
            <CardFooterRight>
              <CloseButton onClick={props.onCancel} />
            </CardFooterRight>
          </CardHeader>
          <CardBody>
            {loading && <LinearLoading />}
            {currentState === EnrollmentState.CREATE_SECOND_FACTOR && (
              <FormContainer as='form' onSubmit={submit}>
                <ControlSpacer>
                  <TextField
                    label='Display Name'
                    value={displayName}
                    onChange={setDisplayName}
                    name='displayName'
                    placeholder='Enter a name for this second factor (ex. Work Phone)...'
                    type='text'
                  />
                </ControlSpacer>
                <ControlSpacer>
                  <ControlLabel>Second Factor Type</ControlLabel>
                  <SecondFactorSelect
                    value={factorType}
                    onChange={setFactorType}
                  />
                </ControlSpacer>

                <ControlSpacer>
                  {factorType.value === 'sms' && (
                    <React.Fragment>
                      <TextField
                        label='Phone Number'
                        value={phoneNumber}
                        onChange={(value: string): void =>
                          setPhoneNumber(value.replace(/\D/g, ''))
                        }
                        name='phoneNumber'
                        placeholder='Enter the phone number (ex. 16174858378)...'
                        type='tel'
                      />
                      <HelpText style={{ marginTop: '5px' }}>
                        Phone number must included country code.
                      </HelpText>
                    </React.Fragment>
                  )}
                  {factorType.value === 'totp' && (
                    <React.Fragment>
                      <QRCode
                        onTotpSecretChange={setTotpSecret}
                        user={props.user}
                      />
                      <HelpText style={{ marginTop: '5px' }}>
                        Scan the QR code with your authenticator app and press
                        Save to verify.
                      </HelpText>
                    </React.Fragment>
                  )}
                </ControlSpacer>
                {error && <ErrorText>{error}</ErrorText>}
              </FormContainer>
            )}
            {currentState === EnrollmentState.VERIFY_SECOND_FACTOR && (
              <FormContainer as='form' onSubmit={submit}>
                <ControlSpacer>
                  <TextField
                    label='Verification Code'
                    value={verificationCode}
                    onChange={setVerificationCode}
                    name='displayName'
                    placeholder='Enter the verification code...'
                    type='text'
                  />
                </ControlSpacer>
              </FormContainer>
            )}
          </CardBody>
          <CardFooter surface={8}>
            <CardFooterLeft />
            <CardFooterRight>
              {currentState === EnrollmentState.CREATE_SECOND_FACTOR && (
                <Button
                  onClick={submit}
                  type='submit'
                  title='Submit'
                  disabled={!factorSubmittable}
                 />
              )}
              {currentState === EnrollmentState.VERIFY_SECOND_FACTOR && (
                <Button
                  onClick={completeEnrollment}
                  type='submit'
                  title='Submit'
                  disabled={!verificationSubmittable}
                 />
              )}
            </CardFooterRight>
          </CardFooter>
        </Column>
      </InnerPopupCard>
    );
  }
);

const HelpText = styled.div`
  color: var(--color-secondary-text);
`;
